// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import { GridItemProps } from "@insite/mobius/GridItem";
import { IconPresentationProps } from "@insite/mobius/Icon";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { getNotifyStyle } from "@steiner/App/Components/Notify";
import { QtyFieldStyle } from "@steiner/App/Components/Qty/QtyField";
import xProductPartNumbersStyles from "@steiner/App/SharedStyles/ProductPartNumbersStyles";
import { ProductAvailabilityStyles } from "@steiner/Overrides/Components/ProductAvailability";
import { CartLineNotesStyles } from "@steiner/Overrides/Widgets/Cart/CartLineNotes";
import Plus from "@steiner/XDL/Icons/Plus";
import StarX from "@steiner/XDL/Icons/StarX";
import { fontConfig } from "@steiner/XDL/PreStyle/typography";
import { color, ellipsis, mergeWithCss, xScreen } from "@steiner/XDL/Utilities";
import { css } from "styled-components";

export interface CartLineSharedStyles {
    productImage?: ProductImageStyles;
    productDescription?: ProductDescriptionStyles;
    productPartNumbers?: ProductPartNumbersStyles;
    addToListLink?: LinkPresentationProps;
    productPrice?: ProductPriceStyles;
    extendedUnitNetPriceText?: SmallHeadingAndTextStyles;
    costCodeGridItem?: GridItemProps;
    costCodeLabelText?: TypographyPresentationProps;
    costCodeValueText?: TypographyPresentationProps;
    promotionNameText?: InjectableCss;
    removeCartLineIcon?: IconPresentationProps;
    cartLineNotes?: CartLineNotesStyles;
    cartLineErrorMessageGridItem?: GridItemProps;
    errorMessage?: TypographyProps;
    productAvailability?: ProductAvailabilityStyles;
    configurationContainer?: InjectableCss;
    configurationOptionText?: TypographyProps;
    qtyFieldStyles?: QtyFieldStyle;
    unitOfMeasure?: TypographyProps;
    promotionIcon?: IconPresentationProps;
}

const sharedLinkProductStyle: LinkPresentationProps = {
    mergeCss: true,
    typographyProps: {
        size: 14,
        lineHeight: "18px",
        css: css`
            min-height: 54px;
            &:not(.no-ellipses) {
                ${ellipsis(3)}

                ${xScreen("lte", "md")} {
                    min-height: initial;
                    ${ellipsis(2)}
                }
            }
        `,
    },
    css: css`
        @media print {
            display: block;
            width: 100%;
        }
    `,
};

const cartLineSharedStyles: CartLineSharedStyles = {
    productImage: {
        linkWrappingImage: {
            css: css`
                width: 64px;
                height: 64px;
                background-color: ${color("common.white")};
                justify-content: center;
                ${xScreen("lte", "md")} {
                    width: 99px;
                    height: 99px;
                }
            `,
        },
    },
    productDescription: {
        productDetailLink: {
            ...sharedLinkProductStyle,
        },
        productDetailDescription: {
            ...sharedLinkProductStyle.typographyProps,
        },
    },
    productPartNumbers: {
        container: {
            css: css`
                margin-bottom: 0px;
                flex-grow: 0;

                @media print {
                    display: block;
                    width: 100%;
                }
            `,
        },
    },
    productPrice: {
        priceLabelText: {
            size: 12,
            weight: fontConfig.weight.regular,
            css: css`
                line-height: 15px;
                text-transform: capitalize;
                padding: 0;
                margin-bottom: 4px;
                display: none;
                ${xScreen("lte", "md")} {
                    display: block;
                }
                @media print {
                    &&& {
                        font-size: 14px;
                        line-height: 16px;
                        display: block;
                    }
                }
            `,
        },
        priceWrapper: {
            css: css`
                flex-direction: column;
                align-items: flex-start;
            `,
        },
        price: {
            priceText: {
                size: 16,
                lineHeight: "22px",
                weight: fontConfig.weight.semiBold,
                css: css`
                    margin-bottom: 0;
                    display: inline;
                    white-space: nowrap;
                `,
            },
            unitOfMeasure: {
                unitOfMeasureText: {
                    size: 16,
                    lineHeight: "22px",
                    css: css`
                        white-space: nowrap;
                    `,
                },
            },
        },
        savingsMessageText: {
            size: 12,
            lineHeight: "15px",
            css: css`
                text-decoration: line-through;
                margin-top: 4px;
            `,
        },
    },
    extendedUnitNetPriceText: {
        extendedStyles: {
            heading: {
                size: 12,
                lineHeight: "15px",
                weight: fontConfig.weight.regular,
                css: css`
                    text-transform: capitalize;
                    padding: 0;
                    margin-bottom: 4px;
                    display: none;
                    ${xScreen("lte", "md")} {
                        display: block;
                    }
                    @media print {
                        &&& {
                            font-size: 14px;
                            line-height: 16px;
                            display: block;
                        }
                    }
                `,
            },
            text: {
                forwardAs: "p",
                size: 16,
                lineHeight: "22px",
                color: "primary",
                weight: fontConfig.weight.semiBold,
                css: css`
                    text-align: start;
                    display: flex;
                    margin-bottom: 0;
                `,
            },
            wrapper: {
                css: css`
                    width: fit-content;
                `,
            },
        },
    },
    addToListLink: {
        typographyProps: {
            size: 12,
            lineHeight: "15px",
            weight: fontConfig.weight.semiBold,
        },
        icon: {
            iconProps: {
                size: 12,
                src: Plus,
            },
        },
    },
    costCodeGridItem: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    costCodeLabelText: {
        ...xProductPartNumbersStyles.label,
    },
    costCodeValueText: {
        ...xProductPartNumbersStyles.value,
    },
    configurationContainer: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 4px;
        `,
    },
    configurationOptionText: {
        ...xProductPartNumbersStyles.value,
    },
    promotionNameText: {
        ...mergeWithCss(xProductPartNumbersStyles.value, {
            css: css`
                background: ${color("common.ui14")};
                padding: 4px;
                display: inline-flex;
                gap: 4px;
                align-items: center;
                margin-top: 8px;
            `,
        }),
    },
    promotionIcon: {
        src: StarX,
        color: "secondary",
        size: 12,
    },
    productAvailability: {
        container: {
            css: css`
                margin-top: 5px;
            `,
        },
    },
    removeCartLineIcon: {
        src: "X",
        size: 14,
        color: "common.ui5",
        css: css`
            &:hover {
                color: ${color("common.ui9")};
            }
        `,
    },
    cartLineNotes: {
        wrapper: {
            css: css`
                width: auto;
                ${xScreen("lte", "md")} {
                    width: auto;
                }
            `,
        },
        editableNotesTextArea: {
            cssOverrides: {
                inputSelect: css`
                    max-height: 80px;
                    margin-top: 8px;
                `,
                formField: css`
                    &&& textarea:disabled {
                        background: ${color("common.ui15")};
                        border-color: ${color("common.ui15")};
                        opacity: 1;
                    }
                `,
            },
        },
        linksWrapper: {
            css: css`
                display: flex;
                justify-content: space-between;
                align-items: center;
                flex-wrap: wrap;
                gap: 10px;
            `,
        },
        showHideLink: {
            typographyProps: {
                size: 12,
                lineHeight: "15px",
            },
        },
        addToProjectsLink: {
            typographyProps: {
                size: 12,
                lineHeight: "15px",
            },
            icon: {
                iconProps: {
                    size: 12,
                    src: Plus,
                },
            },
        },
    },
    cartLineErrorMessageGridItem: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 4px;
            ${xScreen("lte", "md")} {
                margin-top: 10px;
            }
        `,
    },
    errorMessage: {
        color: "danger",
        size: 12,
        lineHeight: "15px",
        weight: fontConfig.weight.semiBold,
        css: css`
            white-space: nowrap;
            margin-top: 10px;
        `,
    },
    qtyFieldStyles: {
        labelText: {
            css: css`
                ${xScreen("gt", "md")} {
                    display: none;
                }

                @media print {
                    &&& {
                        display: block;
                        font-size: 14px;
                        line-height: 16px;
                        padding-left: 32px;
                    }
                }
            `,
        },
    },
    unitOfMeasure: {
        size: fontConfig.fontSize.xSmall,
        weight: fontConfig.weight.regular,
        color: "primary",
        css: css`
            line-height: ${fontConfig.lineHeight.small};
            text-transform: capitalize;
        `,
    },
};

export default cartLineSharedStyles;
